/**
 * REF-LINEAGE REF-3 — resolve a `[Ref N]` reference to its underlying
 * `LinkedDocumentRef` when the reference is sourced from a user-uploaded
 * document with page/section lineage.
 *
 * The references[] array carries `DataSource` rows; each row's `field`
 * is the raw provider/section key (e.g. 'climate', 'grid', 'flood').
 * When the SED's matching section was filled by `extractFromDocument`
 * (REF-1), its `linkedDocumentRefs[0]` carries `docId + pageNumber +
 * sectionHeading + excerptPreview`. This helper looks that up and
 * returns it for the RefLink click handler to consume; it returns
 * `undefined` for any reference that does NOT resolve to a doc with
 * page lineage:
 *
 *  - external URL refs (no `field` in SED_SECTION_IDS),
 *  - knowledge-base entries / ADAMS / external literature / siteResearch
 *    (`field` shape doesn't match a SED section id),
 *  - doc-backed sections where every `linkedDocumentRef` lacks
 *    `pageNumber` (legacy SEDs, DM4-only refs without lineage).
 *
 * In all of those cases the caller falls back to today's
 * scroll-to-appendix behaviour (the deep-link is only fired when this
 * helper returns a non-undefined ref).
 *
 * Pure projection — never mutates the SED, never throws.
 *
 * Preference order when more than one ref carries pageNumber:
 *   1. The one with role === 'primary'.
 *   2. Otherwise the highest-score ref.
 *
 * Composability notes:
 *   - This helper is the single decision point for the deep-link path.
 *     If you add a new "doc ref carries page" shape elsewhere (e.g. a
 *     future DM4 extension), wire it through here so RefLink and the
 *     appendix preview agree on what counts.
 *   - Imported by `walkCitationText` (REF-3c) and by
 *     `ReferencesSection.renderRow` (REF-4). Both surfaces consume the
 *     same return value so the click-through and the inline preview
 *     describe the same passage.
 */
import type { DataSource } from '../types/siteRag';
import type {
  SiteEvidenceDocument,
  LinkedDocumentRef,
  SedSectionId,
} from '../types/siteEvidence';
import { SED_SECTION_IDS } from '../types/siteEvidence';

const SECTION_ID_SET: ReadonlySet<string> = new Set<string>(SED_SECTION_IDS as readonly string[]);

/** Exact uploaded-document identity does not imply a known citation page. */
export function resolveReferencedDocumentId(
  ref: DataSource | undefined,
  sed: SiteEvidenceDocument | undefined,
): string | undefined {
  if (!ref || !sed) return undefined;
  if (ref.field !== 'documents') return resolveDocumentLineage(ref, sed)?.docId;
  const urlId = ref.url?.startsWith('doc://') ? ref.url.slice('doc://'.length) : undefined;
  if (ref.url && !urlId) return undefined;
  if (ref.documentId && urlId && ref.documentId !== urlId) return undefined;
  const id = ref.documentId ?? urlId;
  if (!id || !sed.uploadedDocuments?.some(doc => doc.docId === id)) return undefined;
  return id;
}

export function resolveDocumentLineage(
  ref: DataSource | undefined,
  sed: SiteEvidenceDocument | undefined,
): LinkedDocumentRef | undefined {
  if (!ref || !sed) return undefined;
  if (ref.field === 'documents') {
    const id = resolveReferencedDocumentId(ref, sed);
    if (!id || !Number.isInteger(ref.pageNumber) || (ref.pageNumber ?? 0) < 1) return undefined;
    const doc = sed.uploadedDocuments?.find(candidate => candidate.docId === id);
    // An explicit reference page must exist in this frozen source. Do not
    // infer a page from an unrelated section's link to the same document.
    if (!doc?.pages?.some(page => page.page === ref.pageNumber)) return undefined;
    return { docId: id, pageNumber: ref.pageNumber, role: 'supporting', score: 0,
      rationale: 'Explicit document reference page', evidenceQuotes: [] };
  }
  if (!SECTION_ID_SET.has(ref.field)) return undefined;

  // Index the SED dynamically by field — the SED is a strongly-typed
  // record of EvidenceSection<*> slots; widen to a structural map so
  // we can address slots by string key without a 17-arm switch.
  const slot = (sed as unknown as Record<string, { linkedDocumentRefs?: LinkedDocumentRef[] } | undefined>)[
    ref.field as SedSectionId
  ];
  const refs = slot?.linkedDocumentRefs ?? [];
  if (refs.length === 0) return undefined;

  // Requirement-scoped professional references carry the exact document id.
  // Prefer that identity over the legacy section-level primary/high-score
  // heuristic so two ALTA/geotechnical reports never deep-link to each other.
  const scopedRefs = ref.documentId
    ? refs.filter(candidate => candidate.docId === ref.documentId)
    : refs;
  if (scopedRefs.length === 0) return undefined;

  // Require SOMETHING to deep-link to: a page number (REF-2) OR a
  // verbatim-passage char span (H-4 `sourceOffset`). A ref with neither
  // is legacy (pre-REF-2) and falls back to today's scroll-to-appendix
  // behaviour. H-4 widens this from page-only so an offset-only ref (a
  // doc with no detectable page structure but a verbatim excerpt match)
  // is still deep-linkable to the exact passage.
  const usable = scopedRefs.filter(r => hasDeepLinkTarget(r));
  if (usable.length === 0) return undefined;

  // Preference order: primary first; otherwise highest-score.
  const primary = usable.find(r => r.role === 'primary');
  if (primary) return primary;
  return [...usable].sort((a, b) => b.score - a.score)[0];
}

/**
 * H-4 — true when a ref carries a position the deep-link can resolve:
 * a valid page number (REF-2) or a valid passage char span (H-4). The
 * offset validity mirrors `resolveCitationDeepLink.isValidOffset`
 * (finite, non-negative, `end > start`).
 */
function hasDeepLinkTarget(r: LinkedDocumentRef): boolean {
  if (typeof r.pageNumber === 'number' && r.pageNumber > 0) return true;
  const o = r.sourceOffset;
  return (
    !!o
    && typeof o.start === 'number' && typeof o.end === 'number'
    && Number.isFinite(o.start) && Number.isFinite(o.end)
    && o.start >= 0 && o.end > o.start
  );
}
